/** @file briefTextOf: the art brief of an open puppet: its slots, the views, style notes, profile. */
import { deriveSlots } from '@zilo/pose3d-format/derive/deriveSlots';
import { effectiveProfile } from '@zilo/pose3d-format/profile/effectiveProfile';
import { BUILTIN_PROFILES } from '@zilo/pose3d-format/tables/builtinProfiles';
import type { Puppet } from '@zilo/pose3d-format/types/puppet';
import { artBriefOf, type ArtBriefProfile } from './artBriefOf';
import type { BriefMode, SlotViewAngles, ViewedSlot } from './artTypes';
import { guideBriefOf } from './sheet/guideBriefOf';
import { SHEET_FACINGS } from './sheet/sheetFacings';
import { sheetBriefOf } from './sheet/sheetBriefOf';

/** The camera pitch of the puppet's profile; null when the profile is not a built-in one. */
function pitchOf(puppet: Puppet): number | null {
  try {
    return effectiveProfile(puppet, BUILTIN_PROFILES).camera.pitch;
  } catch {
    return null;
  }
}

/** The profile line every brief carries. */
const profileOf = (puppet: Puppet): ArtBriefProfile => ({ id: puppet.profile, pitchDeg: pitchOf(puppet) });

/**
 * The brief of `mode` (default `slots`): `artBriefOf` over `deriveSlots(puppet)` (the slots.json source) with `views`
 * attached and the slot count shown; `sheet` and `guide` (`sheetBriefOf`, `guideBriefOf`) count the facings instead.
 */
export function briefTextOf(puppet: Puppet, views: ReadonlyMap<string, SlotViewAngles>, style: string, includeFilled: boolean, mode: BriefMode = 'slots'): { text: string; slots: number; unit: 'slots' | 'sheets' } {
  if (mode === 'sheet') return { text: sheetBriefOf(puppet, style, profileOf(puppet)), slots: SHEET_FACINGS.length, unit: 'sheets' };
  if (mode === 'guide') return { text: guideBriefOf(puppet, style, profileOf(puppet)), slots: SHEET_FACINGS.length, unit: 'sheets' };
  const slots: ViewedSlot[] = deriveSlots(puppet).map((s) => {
    const view = views.get(s.key);
    return view === undefined ? s : { ...s, view };
  });
  const text = artBriefOf(slots, style, profileOf(puppet), { includeFilled });
  return { text, slots: slots.filter((s) => includeFilled || s.status === 'empty').length, unit: 'slots' };
}
